import React from "react";
import { Tabs, Tab } from "react-bootstrap";

/**
 * Three-tab shell for the Finance & Statistics report (D3/D4):
 *   Tab 3 "Finance & Statistics" (default), Tab 1 "Financial Breakdown",
 *   Tab 2 "District In-House Memo" (hidden at nation).
 * Tab 3 is mounted on load; Tabs 1–2 mount on first open (`mountOnEnter`) and
 * fire `onOpen(key)` so the host can lazy-fetch their data.
 */

export type FinanceTabKey = "statistics" | "breakdown" | "inhouse";

type Props = {
  /** Nation hides Tab 2 (no district-in-house endpoint). */
  showInHouse: boolean;
  renderStatistics: () => React.ReactNode;
  renderBreakdown: () => React.ReactNode;
  renderInHouse?: () => React.ReactNode;
  /** Called the first time a tab is shown — used for lazy fetching. */
  onOpen?: (key: FinanceTabKey) => void;
};

const FinanceStatisticsTabs: React.FC<Props> = ({ showInHouse, renderStatistics, renderBreakdown, renderInHouse, onOpen }) => {
  const [active, setActive] = React.useState<FinanceTabKey>("statistics");
  const opened = React.useRef<Set<FinanceTabKey>>(new Set(["statistics"]));

  React.useEffect(() => {
    onOpen?.("statistics");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSelect = (key: string | null) => {
    const k = (key as FinanceTabKey) ?? "statistics";
    setActive(k);
    if (!opened.current.has(k)) {
      opened.current.add(k);
      onOpen?.(k);
    }
  };

  return (
    <Tabs activeKey={active} onSelect={handleSelect} mountOnEnter className="mb-3">
      <Tab eventKey="statistics" title="Finance & Statistics">
        {renderStatistics()}
      </Tab>
      <Tab eventKey="breakdown" title="Financial Breakdown">
        {renderBreakdown()}
      </Tab>
      {showInHouse ? (
        <Tab eventKey="inhouse" title="District In-House Memo">
          {renderInHouse?.()}
        </Tab>
      ) : null}
    </Tabs>
  );
};

export default FinanceStatisticsTabs;
